import { X, Pencil } from "lucide-react";
import type { Employee, CoverageStatus } from "../types/form";

interface EmployeeCardProps {
  employee: Employee;
  offersDental: boolean;
  offersVision: boolean;
  availablePlans: number;
  onEdit: () => void;
  onDelete: () => void;
  actionsDisabled?: boolean;
  hasError?: boolean;
}

function statusDisplay(value: CoverageStatus): string {
  if (!value || value === "N/A") return "—";
  return value;
}

export function EmployeeCard({ employee, offersDental, offersVision, availablePlans, onEdit, onDelete, actionsDisabled, hasError }: EmployeeCardProps) {
  const name = `${employee.lastName}, ${employee.firstName}`;
  const showPlan = employee.medicalCoverage === "Enrolling" && employee.medicalPlan;

  return (
    <div className={`rounded-[14px] bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden ${hasError ? "border border-danger" : "border border-gray-200"}`}>
      {hasError && <span className="sr-only">Has validation errors</span>}
      <div className={`flex items-center gap-2 px-3 py-2 border-b ${hasError ? "border-danger bg-danger-bg" : "border-brand-green-light bg-brand-green-light"}`}>
        <span className="flex-1 min-w-0 text-sm font-bold text-gray-800 truncate">{name}</span>
        {employee.owner === "Yes" && (
          <span className="px-[7px] py-[2px] text-[10px] font-bold uppercase tracking-wider border border-brand-navy text-brand-navy bg-white rounded-full shrink-0">
            Owner
          </span>
        )}
        {employee.cobra === "Yes" && (
          <span className="px-[7px] py-[2px] text-[10px] font-bold uppercase tracking-wider border border-brand-navy text-brand-navy bg-white rounded-full shrink-0">
            COBRA
          </span>
        )}
        <button
          onClick={actionsDisabled ? undefined : onEdit}
          disabled={actionsDisabled}
          className={`w-7 h-7 flex items-center justify-center rounded-full bg-white shrink-0 transition-all ${actionsDisabled ? "text-gray-200 cursor-default" : "text-gray-400 hover:bg-blue-50 hover:text-blue-600 cursor-pointer"}`}
          aria-label={`Edit ${employee.firstName} ${employee.lastName}`}
        >
          <Pencil size={13} strokeWidth={2.5} />
        </button>
        <button
          onClick={actionsDisabled ? undefined : onDelete}
          disabled={actionsDisabled}
          className={`w-7 h-7 flex items-center justify-center rounded-full bg-white shrink-0 transition-all ${actionsDisabled ? "text-gray-200 cursor-default" : "text-gray-400 hover:bg-red-50 hover:text-danger cursor-pointer"}`}
          aria-label={`Delete ${employee.firstName} ${employee.lastName}`}
        >
          <X size={15} strokeWidth={2.5} />
        </button>
      </div>

      {/* Coverage grid — visible labels (Medical/Dental/Vision) + values are sufficient for AT;
          dropping the redundant role="list" + role="listitem" + aria-label combo so screen readers
          don't read the value twice. */}
      <div className="grid grid-cols-3 gap-2 px-3.5 py-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">Medical</div>
          <div className="text-xs font-semibold text-gray-700">{statusDisplay(employee.medicalCoverage)}</div>
          {employee.medicalCoverage === "Enrolling" && availablePlans > 1 && !employee.medicalPlan
            ? <div className="text-[11px] text-danger mt-0.5">Plan required</div>
            : showPlan && <div className="text-[11px] text-gray-400 mt-0.5">{employee.medicalPlan}</div>}
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">Dental</div>
          {offersDental && (!employee.dentalCoverage || employee.dentalCoverage === "N/A")
            ? <div className="text-xs text-danger">Required</div>
            : <div className="text-xs font-semibold text-gray-700">{offersDental ? statusDisplay(employee.dentalCoverage) : "—"}</div>}
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">Vision</div>
          {offersVision && (!employee.visionCoverage || employee.visionCoverage === "N/A")
            ? <div className="text-xs text-danger">Required</div>
            : <div className="text-xs font-semibold text-gray-700">{offersVision ? statusDisplay(employee.visionCoverage) : "—"}</div>}
        </div>
      </div>
    </div>
  );
}
